import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { updatedEntities, denormalisedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';

import { fetchCurrentUser } from '../../ducks/user.duck';

// The Marketplace API allows a maximum of 100 results per page.
// We fetch the owner's full catalog up-front (up to MAX_FETCH_PAGES pages) so that
// filtering, sorting, searching and per-state counts can all be done client-side and
// stay exact and instant for the typical host. Beyond the cap we degrade gracefully
// (fetchedAll = false) and show what we have.
const FETCH_ALL_PAGE_SIZE = 100;
const MAX_FETCH_PAGES = 10; // safety cap: up to 1000 listings loaded client-side

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Async Thunks ================ //

////////////////////////////
// Query All Own Listings //
////////////////////////////
// Fetches every page of the current user's own listings (across all states) up to the
// MAX_FETCH_PAGES cap, dispatching entities for each page and returning the ordered list
// of result IDs plus pagination metadata.
const queryAllOwnListingsPayloadCreator = async (
  queryParams,
  { extra: sdk, dispatch, rejectWithValue }
) => {
  try {
    const firstResponse = await sdk.ownListings.query({
      ...queryParams,
      page: 1,
      perPage: FETCH_ALL_PAGE_SIZE,
    });
    dispatch(addOwnEntities(firstResponse));

    const meta = firstResponse.data.meta;
    const totalPages = meta.totalPages || 1;
    const fetchedAll = totalPages <= MAX_FETCH_PAGES;
    let ids = firstResponse.data.data.map(l => l.id);

    if (totalPages > 1) {
      const lastPage = Math.min(totalPages, MAX_FETCH_PAGES);
      const pageNumbers = [];
      for (let p = 2; p <= lastPage; p++) {
        pageNumbers.push(p);
      }
      const responses = await Promise.all(
        pageNumbers.map(page =>
          sdk.ownListings.query({ ...queryParams, page, perPage: FETCH_ALL_PAGE_SIZE })
        )
      );
      responses.forEach(response => {
        dispatch(addOwnEntities(response));
        ids = ids.concat(response.data.data.map(l => l.id));
      });
    }

    return { ids, meta, fetchedAll };
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const queryAllOwnListingsThunk = createAsyncThunk(
  'app/ManageListingsPage/queryAllOwnListings',
  queryAllOwnListingsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const queryAllOwnListings = queryParams => (dispatch, getState, sdk) => {
  return dispatch(queryAllOwnListingsThunk(queryParams)).unwrap();
};

///////////////////
// Close Listing //
///////////////////
const closeListingPayloadCreator = (listingId, { extra: sdk, rejectWithValue }) => {
  return sdk.ownListings
    .close({ id: listingId }, { expand: true })
    .then(response => response)
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const closeListingThunk = createAsyncThunk(
  'app/ManageListingsPage/closeListing',
  closeListingPayloadCreator
);
// Backward compatible wrapper for the thunk
export const closeListing = listingId => (dispatch, getState, sdk) => {
  return dispatch(closeListingThunk(listingId)).unwrap();
};

//////////////////
// Open Listing //
//////////////////
const openListingPayloadCreator = (listingId, { extra: sdk, rejectWithValue }) => {
  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => response)
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const openListingThunk = createAsyncThunk(
  'app/ManageListingsPage/openListing',
  openListingPayloadCreator
);
// Backward compatible wrapper for the thunk
export const openListing = listingId => (dispatch, getState, sdk) => {
  return dispatch(openListingThunk(listingId)).unwrap();
};

///////////////////
// Discard Draft //
///////////////////

const delay = ms => new Promise(resolve => window.setTimeout(resolve, ms));

const discardDraftPayloadCreator = (listingId, thunkAPI) => {
  const { extra: sdk, rejectWithValue } = thunkAPI;

  return sdk.ownListings
    .discardDraft({ id: listingId }, { expand: true })
    .then(() => {
      // Small delay so that the user notices which listing gets removed
      return delay(300);
    })
    .then(() => ({ listingId }))
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const discardDraftThunk = createAsyncThunk(
  'app/ManageListingsPage/discardDraft',
  discardDraftPayloadCreator
);
// Backward compatible wrapper for the thunk
export const discardDraft = listingId => (dispatch, getState, sdk) => {
  return dispatch(discardDraftThunk(listingId)).unwrap();
};

// ================ Slice ================ //

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const manageListingsPageSlice = createSlice({
  name: 'ManageListingsPage',
  initialState: {
    pagination: null,
    queryParams: null,
    queryInProgress: false,
    queryListingsError: null,
    // All own-listing IDs fetched up-front (across every state), in newest-first order.
    allResultIds: [],
    // Whether the full catalog could be loaded within the page cap.
    fetchedAll: false,
    ownEntities: {},
    openingListing: null,
    openingListingError: null,
    closingListing: null,
    closingListingError: null,
    discardingDraft: null,
    discardingDraftError: null,
  },
  reducers: {
    clearOpenListingError: state => {
      state.openingListing = null;
      state.openingListingError = null;
    },
    addOwnEntities: (state, action) => {
      // This works the same way as addMarketplaceEntities,
      // but we don't want to mix own listings with searched listings
      // (own listings data contains different info - e.g. exact location etc.)
      const apiResponse = action.payload.data;
      state.ownEntities = updatedEntities({ ...state.ownEntities }, apiResponse);
    },
  },
  extraReducers: builder => {
    // Query all own listings
    builder
      .addCase(queryAllOwnListingsThunk.pending, (state, action) => {
        state.queryParams = action.meta.arg;
        state.queryInProgress = true;
        state.queryListingsError = null;
        state.allResultIds = [];
        state.fetchedAll = false;
      })
      .addCase(queryAllOwnListingsThunk.fulfilled, (state, action) => {
        state.allResultIds = action.payload.ids;
        state.pagination = action.payload.meta;
        state.fetchedAll = action.payload.fetchedAll;
        state.queryInProgress = false;
      })
      .addCase(queryAllOwnListingsThunk.rejected, (state, action) => {
        // eslint-disable-next-line no-console
        console.error(action.payload || action.error);
        state.queryInProgress = false;
        state.queryListingsError = action.payload;
      });

    // Open listing
    builder
      .addCase(openListingThunk.pending, (state, action) => {
        state.openingListing = action.meta.arg;
        state.openingListingError = null;
      })
      .addCase(openListingThunk.fulfilled, (state, action) => {
        const listing = action.payload.data.data;
        const updatedState = updateListingAttributes(state, listing);
        state.ownEntities = updatedState.ownEntities;
        state.openingListing = null;
      })
      .addCase(openListingThunk.rejected, (state, action) => {
        // eslint-disable-next-line no-console
        console.error(action.payload || action.error);
        state.openingListingError = {
          listingId: state.openingListing,
          error: action.payload,
        };
        state.openingListing = null;
      });

    // Close listing
    builder
      .addCase(closeListingThunk.pending, (state, action) => {
        state.closingListing = action.meta.arg;
        state.closingListingError = null;
      })
      .addCase(closeListingThunk.fulfilled, (state, action) => {
        const listing = action.payload.data.data;
        const updatedState = updateListingAttributes(state, listing);
        state.ownEntities = updatedState.ownEntities;
        state.closingListing = null;
      })
      .addCase(closeListingThunk.rejected, (state, action) => {
        // eslint-disable-next-line no-console
        console.error(action.payload || action.error);
        state.closingListingError = {
          listingId: state.closingListing,
          error: action.payload,
        };
        state.closingListing = null;
      });

    // Discard draft
    builder
      .addCase(discardDraftThunk.pending, (state, action) => {
        state.discardingDraft = action.meta.arg;
        state.discardingDraftError = null;
      })
      .addCase(discardDraftThunk.fulfilled, (state, action) => {
        // Remove the discarded draft from the result set; counts are derived from this.
        const discardedId = action.payload.listingId;
        state.allResultIds = state.allResultIds.filter(id => id.uuid !== discardedId.uuid);
        if (state.pagination && typeof state.pagination.totalItems === 'number') {
          state.pagination = {
            ...state.pagination,
            totalItems: Math.max(0, state.pagination.totalItems - 1),
          };
        }
        state.discardingDraft = null;
      })
      .addCase(discardDraftThunk.rejected, (state, action) => {
        // eslint-disable-next-line no-console
        console.error(action.payload || action.error);
        state.discardingDraftError = {
          listingId: state.discardingDraft,
          error: action.payload,
        };
        state.discardingDraft = null;
      });
  },
});

export const { clearOpenListingError, addOwnEntities } = manageListingsPageSlice.actions;
export default manageListingsPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // Filtering, sorting and searching happen client-side over the fetched catalog,
  // so we ignore search params here apart from establishing a sensible default order.
  dispatch(clearOpenListingError());

  // v11.1.0 filters referral params out of the API query here. We don't need that: this page
  // passes no URL params to the API at all (see the note above), so there is nothing to filter.

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(
      queryAllOwnListings({
        // Newest-first by default (own_listings/query supports server-side sort).
        sort: '-createdAt',
        include: ['images', 'currentStock'],
        'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        'limit.images': 1,
      })
    ),
  ])
    .then(response => {
      const ownListings = response[1];
      return ownListings;
    })
    .catch(e => {
      throw e;
    });
};
